import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import AOS from "aos";
import "aos/dist/aos.css";

export default function PrivacyPolicy() {
  const { t, i18n } = useTranslation();

  useEffect(() => {
    AOS.init({ duration: 1200 });

    const seoTitle = t("privacyPolicy.seoTitle");
    const seoDescription = t("privacyPolicy.seoDescription");
    document.title = seoTitle;

    let metaDesc = document.querySelector('meta[name="description"]');
    if (!metaDesc) {
      metaDesc = document.createElement("meta");
      metaDesc.name = "description";
      document.head.appendChild(metaDesc);
    }
    metaDesc.setAttribute("content", seoDescription);

    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }
    canonical.setAttribute("href", "https://www.kanz-alriyada.com/privacy-policy");
  }, [t, i18n.language]);

  const accountItems = t("privacyPolicy.sections.informationYouProvide.accountItems", { returnObjects: true }) || [];
  const businessItems = t("privacyPolicy.sections.businessData.items", { returnObjects: true }) || [];
  const deviceItems = t("privacyPolicy.sections.deviceAndTechnical.items", { returnObjects: true }) || [];
  const howWeUseItems = t("privacyPolicy.sections.howWeUse.items", { returnObjects: true }) || [];
  const howWeShareItems = t("privacyPolicy.sections.howWeShare.items", { returnObjects: true }) || [];
  const rightsItems = t("privacyPolicy.sections.rightsAndChoices.items", { returnObjects: true }) || [];

  return (
    <div className="privacy-page">
      {/* Page Header */}
      <section className="page-header" data-aos="fade-up">
        <h1>{t("privacyPolicy.title")}</h1>
        <p className="privacy-header-subtitle">{t("privacyPolicy.introSubtitle")}</p>
        <span className="privacy-last-updated">{t("privacyPolicy.lastUpdated")}</span>
      </section>

      {/* Main Content */}
      <div className="privacy-container">
        <article className="privacy-card" data-aos="fade-up">
          {/* Section 1: Intro */}
          <section className="privacy-section">
            <h2>{t("privacyPolicy.sections.intro.title")}</h2>
            <p>{t("privacyPolicy.sections.intro.p1")}</p>
            <p>{t("privacyPolicy.sections.intro.p2")}</p>
          </section>

          {/* Section 2: Information We Collect */}
          <section className="privacy-section">
            <h2>{t("privacyPolicy.sections.informationWeCollect.title")}</h2>
            <p>{t("privacyPolicy.sections.informationWeCollect.p1")}</p>
          </section>

          {/* Section 3: Information You Provide */}
          <section className="privacy-section">
            <h2>{t("privacyPolicy.sections.informationYouProvide.title")}</h2>
            <h3>{t("privacyPolicy.sections.informationYouProvide.accountTitle")}</h3>
            <p>{t("privacyPolicy.sections.informationYouProvide.accountDesc")}</p>
            <ul className="privacy-list">
              {Array.isArray(accountItems) && accountItems.map((item, idx) => (
                <li key={idx}>{item}</li>
              ))}
            </ul>
            <h3>{t("privacyPolicy.sections.informationYouProvide.inquiriesTitle")}</h3>
            <p>{t("privacyPolicy.sections.informationYouProvide.inquiriesDesc")}</p>
          </section>

          {/* Section 4: Business and Transaction Information */}
          <section className="privacy-section">
            <h2>{t("privacyPolicy.sections.businessData.title")}</h2>
            <p>{t("privacyPolicy.sections.businessData.p1")}</p>
            <ul className="privacy-list">
              {Array.isArray(businessItems) && businessItems.map((item, idx) => (
                <li key={idx}>{item}</li>
              ))}
            </ul>
          </section>

          {/* Section 5: Device and Technical Information */}
          <section className="privacy-section">
            <h2>{t("privacyPolicy.sections.deviceAndTechnical.title")}</h2>
            <p>{t("privacyPolicy.sections.deviceAndTechnical.p1")}</p>
            <ul className="privacy-list">
              {Array.isArray(deviceItems) && deviceItems.map((item, idx) => (
                <li key={idx}>{item}</li>
              ))}
            </ul>
            <div className="privacy-callout">
              <p>{t("privacyPolicy.sections.deviceAndTechnical.noCollectNote")}</p>
            </div>
          </section>

          {/* Section 6: Push Notifications */}
          <section className="privacy-section">
            <h2>{t("privacyPolicy.sections.pushNotifications.title")}</h2>
            <p>{t("privacyPolicy.sections.pushNotifications.p1")}</p>
            <p>{t("privacyPolicy.sections.pushNotifications.p2")}</p>
          </section>

          {/* Section 7: How We Use Information */}
          <section className="privacy-section">
            <h2>{t("privacyPolicy.sections.howWeUse.title")}</h2>
            <p>{t("privacyPolicy.sections.howWeUse.p1")}</p>
            <ul className="privacy-list">
              {Array.isArray(howWeUseItems) && howWeUseItems.map((item, idx) => (
                <li key={idx}>{item}</li>
              ))}
            </ul>
          </section>

          {/* Section 8: How We Share Information */}
          <section className="privacy-section">
            <h2>{t("privacyPolicy.sections.howWeShare.title")}</h2>
            <p className="privacy-highlight-text">{t("privacyPolicy.sections.howWeShare.confidentiality")}</p>
            <h3>{t("privacyPolicy.sections.howWeShare.disclosuresTitle")}</h3>
            <ul className="privacy-list">
              {Array.isArray(howWeShareItems) && howWeShareItems.map((item, idx) => (
                <li key={idx}>{item}</li>
              ))}
            </ul>
          </section>

          {/* Section 9: Third-Party Services */}
          <section className="privacy-section">
            <h2>{t("privacyPolicy.sections.thirdPartyServices.title")}</h2>
            <p>{t("privacyPolicy.sections.thirdPartyServices.p1")}</p>

            <div className="privacy-subsection-box">
              <h3>{t("privacyPolicy.sections.thirdPartyServices.mobileTitle")}</h3>
              <ul className="privacy-list">
                <li>{t("privacyPolicy.sections.thirdPartyServices.mobileFCM")}</li>
                <li>{t("privacyPolicy.sections.thirdPartyServices.mobileAPNs")}</li>
              </ul>
              <div className="privacy-callout">
                <p>{t("privacyPolicy.sections.thirdPartyServices.mobileNote")}</p>
              </div>
            </div>

            <div className="privacy-subsection-box">
              <h3>{t("privacyPolicy.sections.thirdPartyServices.webTitle")}</h3>
              <p>{t("privacyPolicy.sections.thirdPartyServices.webFonts")}</p>
            </div>
          </section>

          {/* Section 10: Data Storage and Security */}
          <section className="privacy-section">
            <h2>{t("privacyPolicy.sections.security.title")}</h2>
            <p>{t("privacyPolicy.sections.security.p1")}</p>
            <p>{t("privacyPolicy.sections.security.p2")}</p>
          </section>

          {/* Section 11: Data Retention */}
          <section className="privacy-section">
            <h2>{t("privacyPolicy.sections.dataRetention.title")}</h2>
            <p>{t("privacyPolicy.sections.dataRetention.p1")}</p>
            <p>{t("privacyPolicy.sections.dataRetention.p2")}</p>
          </section>

          {/* Section 12: Account Deletion and Data Management */}
          <section className="privacy-section">
            <h2>{t("privacyPolicy.sections.accountDeletion.title")}</h2>
            <p>{t("privacyPolicy.sections.accountDeletion.p1")}</p>
            <h3>{t("privacyPolicy.sections.accountDeletion.howToRequestTitle")}</h3>
            <p>{t("privacyPolicy.sections.accountDeletion.howToRequest")}</p>
            <h3>{t("privacyPolicy.sections.accountDeletion.scopeTitle")}</h3>
            <p>{t("privacyPolicy.sections.accountDeletion.scope")}</p>
            <div className="privacy-callout">
              <p>{t("privacyPolicy.sections.accountDeletion.appleNotice")}</p>
            </div>
          </section>

          {/* Section 13: Your Rights and Choices */}
          <section className="privacy-section">
            <h2>{t("privacyPolicy.sections.rightsAndChoices.title")}</h2>
            <ul className="privacy-list">
              {Array.isArray(rightsItems) && rightsItems.map((item, idx) => (
                <li key={idx}>{item}</li>
              ))}
            </ul>
          </section>

          {/* Section 14: Children's Privacy */}
          <section className="privacy-section">
            <h2>{t("privacyPolicy.sections.childrensPrivacy.title")}</h2>
            <p>{t("privacyPolicy.sections.childrensPrivacy.p1")}</p>
          </section>

          {/* Section 15: International Data Transfers */}
          <section className="privacy-section">
            <h2>{t("privacyPolicy.sections.transfers.title")}</h2>
            <p>{t("privacyPolicy.sections.transfers.p1")}</p>
          </section>

          {/* Section 16: Changes to This Privacy Policy */}
          <section className="privacy-section">
            <h2>{t("privacyPolicy.sections.changes.title")}</h2>
            <p>{t("privacyPolicy.sections.changes.p1")}</p>
          </section>

          {/* Section 17: Contact Us */}
          <section className="privacy-section privacy-contact-section">
            <h2>{t("privacyPolicy.sections.contact.title")}</h2>
            <p>{t("privacyPolicy.sections.contact.p1")}</p>

            <div className="privacy-contact-card">
              <h3>{t("privacyPolicy.sections.contact.companyName")}</h3>
              <div className="privacy-contact-grid">
                <div className="privacy-contact-item">
                  <span className="contact-icon">✉️</span>
                  <div>
                    <strong>{t("privacyPolicy.sections.contact.emailLabel")}: </strong>
                    <a href={`mailto:${t("privacyPolicy.sections.contact.email")}`}>
                      {t("privacyPolicy.sections.contact.email")}
                    </a>
                  </div>
                </div>
                <div className="privacy-contact-item">
                  <span className="contact-icon">📞</span>
                  <div>
                    <strong>{t("privacyPolicy.sections.contact.phoneLabel")}: </strong>
                    <a href={`tel:${t("privacyPolicy.sections.contact.phone1")}`}>
                      {t("privacyPolicy.sections.contact.phone1")}
                    </a>
                    {" / "}
                    <a href={`tel:${t("privacyPolicy.sections.contact.phone2")}`}>
                      {t("privacyPolicy.sections.contact.phone2")}
                    </a>
                  </div>
                </div>
                <div className="privacy-contact-item">
                  <span className="contact-icon">📍</span>
                  <div>
                    <strong>{t("privacyPolicy.sections.contact.addressLabel")}: </strong>
                    <span>{t("privacyPolicy.sections.contact.address")}</span>
                  </div>
                </div>
                <div className="privacy-contact-item">
                  <span className="contact-icon">🌐</span>
                  <div>
                    <strong>{t("privacyPolicy.sections.contact.websiteLabel")}: </strong>
                    <a href={t("privacyPolicy.sections.contact.website")} target="_blank" rel="noopener noreferrer">
                      {t("privacyPolicy.sections.contact.website")}
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </article>
      </div>
    </div>
  );
}
